/* ==========================================================================
   PAMPER & PEACE — global.js
   Shared behaviour: header scroll state, announcement rotation, scroll reveals,
   mobile menu, predictive search, toast, and small utilities other modules use.
   Vanilla ES6, no dependencies. Loaded with `defer` so the DOM is ready.
   ========================================================================== */
(function () {
  'use strict';

  const Pamper = (window.Pamper = window.Pamper || {});
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- tiny utils, shared on window.Pamper.utils ---------- */
  const utils = (Pamper.utils = {
    money(cents) {
      const fmt = (Pamper.settings && Pamper.settings.moneyFormat) || '${{amount}}';
      const amount = (cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      const amountNoDecimals = Math.round(cents / 100).toLocaleString();
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount);
    },
    debounce(fn, wait) {
      let t;
      return function (...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    },
    trapFocus(container, opener) {
      const sel =
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
      function onKey(e) {
        if (e.key !== 'Tab') return;
        const items = [...container.querySelectorAll(sel)].filter(
          (el) => el.offsetParent !== null
        );
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
      container.addEventListener('keydown', onKey);
      return () => container.removeEventListener('keydown', onKey);
    },
  });

  /* ---------- toast ---------- */
  let toastTimer;
  Pamper.toast = function (msg) {
    const el = document.getElementById('pp-toast');
    if (!el) return;
    el.textContent = msg;
    el.dataset.show = 'true';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (el.dataset.show = 'false'), 2600);
  };

  /* ---------- header: scroll state + sticky-height sync ---------- */
  (function header() {
    const header = document.querySelector('[data-header]');
    const group = document.querySelector('[data-header-group]');
    const rootEl = document.documentElement;

    // Publish the height of the sticky announcement+header group so in-page
    // sticky elements (PDP gallery, cart, account) clear it. Measured at the
    // top of the page where the header is at full (uncompacted) height.
    const measure = () => {
      if (group) rootEl.style.setProperty('--pp-sticky-h', group.offsetHeight + 'px');
    };
    measure();
    window.addEventListener('resize', utils.debounce(measure, 150), { passive: true });

    if (!header) return;
    let ticking = false;
    const update = () => {
      header.dataset.scrolled = window.scrollY > 12 ? 'true' : 'false';
      ticking = false;
    };
    update();
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
  })();

  /* ---------- announcement rotation (gentle crossfade) ---------- */
  (function announcement() {
    const bar = document.querySelector('[data-announcement]');
    if (!bar || bar.dataset.rotate !== 'true' || reduceMotion) return;
    const slides = [...bar.querySelectorAll('[data-slide]')];
    if (slides.length < 2) return;

    let i = 0;
    let paused = false;
    bar.addEventListener('mouseenter', () => (paused = true));
    bar.addEventListener('mouseleave', () => (paused = false));
    bar.addEventListener('focusin', () => (paused = true));
    bar.addEventListener('focusout', () => (paused = false));

    setInterval(() => {
      if (paused || document.hidden) return;
      slides[i].dataset.active = 'false';
      slides[i].setAttribute('aria-hidden', 'true');
      i = (i + 1) % slides.length;
      slides[i].dataset.active = 'true';
      slides[i].removeAttribute('aria-hidden');
    }, 5000);
  })();

  /* ---------- scroll reveal (staggered) ---------- */
  (function reveals() {
    const els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window) || Pamper.settings.animations === false) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
    // Expose so dynamically-injected nodes (e.g. quiz result) can register.
    Pamper.observeReveals = (root) =>
      (root || document).querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el) => io.observe(el));
  })();

  /* ---------- responsive video: separate mobile / desktop uploads ----------
     A section renders its DESKTOP clip as the <video>'s <source> children and
     hands the MOBILE clip's sources via data-rv-mobile (JSON, see the
     `video-mobile-sources` snippet). On phones (≤ 749px) we swap to the mobile
     set; back on wider screens we restore desktop. Sections that set only one
     clip render it as the default and never get data-rv-mobile, so they play
     that clip everywhere and this helper leaves them untouched. */
  const MOBILE_MQ = window.matchMedia('(max-width: 749px)');
  function wireResponsiveVideo(video) {
    if (video.dataset.rvWired) return;
    video.dataset.rvWired = 'true';
    let mobiles;
    try {
      mobiles = JSON.parse(video.getAttribute('data-rv-mobile') || '[]');
    } catch (e) {
      mobiles = [];
    }
    if (!mobiles.length) return; // no mobile clip → desktop plays everywhere
    // The rendered <source> children are the desktop set; snapshot for restore.
    const desktops = [...video.querySelectorAll('source')].map((s) => ({
      src: s.src,
      type: s.type,
    }));
    if (!video.dataset.rvActive) video.dataset.rvActive = 'desktop';

    const apply = () => {
      const want = MOBILE_MQ.matches ? 'mobile' : 'desktop';
      if (video.dataset.rvActive === want) return;
      const list = want === 'mobile' ? mobiles : desktops;
      if (!list.length) return;
      const wasPlaying = !video.paused;
      [...video.querySelectorAll('source')].forEach((s) => s.remove());
      list.forEach(({ src, type }) => {
        const el = document.createElement('source');
        el.src = src;
        if (type) el.type = type;
        video.appendChild(el);
      });
      video.dataset.rvActive = want;
      video.load();
      if (video.autoplay || wasPlaying) {
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      }
    };

    apply();
    MOBILE_MQ.addEventListener
      ? MOBILE_MQ.addEventListener('change', apply)
      : MOBILE_MQ.addListener(apply);
  }
  Pamper.responsiveVideo = (root) =>
    (root || document)
      .querySelectorAll('video[data-rv-mobile]')
      .forEach(wireResponsiveVideo);
  Pamper.responsiveVideo();

  /* ---------- theme editor: keep re-injected sections visible ----------
     When a merchant edits a section, Shopify rebuilds its DOM via AJAX and
     fires `shopify:section:load`. The fresh [data-reveal] nodes never reach
     the page-load IntersectionObserver above, so they stay at opacity:0 and
     the section looks blank. Reveal them instantly on (re)load so editing is
     always WYSIWYG. Only fires inside the theme editor. */
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target;
    if (!section || !section.querySelectorAll) return;
    section
      .querySelectorAll('[data-reveal]:not(.is-visible)')
      .forEach((el) => el.classList.add('is-visible'));
    // Re-wire any mobile/desktop videos in the freshly rendered section.
    if (Pamper.responsiveVideo) Pamper.responsiveVideo(section);
  });

  /* ---------- generic modal opener (mobile menu + search) ---------- */
  function bindModal({ openSel, closeSel, modalSel, inputSel }) {
    const modal = document.querySelector(modalSel);
    if (!modal) return;
    let release;
    const openers = document.querySelectorAll(openSel);
    const open = (e) => {
      if (e) e.preventDefault();
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      const focusEl = inputSel ? modal.querySelector(inputSel) : modal.querySelector('button, a');
      requestAnimationFrame(() => focusEl && focusEl.focus());
      const opener = e && e.currentTarget;
      release = utils.trapFocus(modal, opener);
      openers.forEach((o) => o.setAttribute('aria-expanded', 'true'));
    };
    const close = () => {
      modal.hidden = true;
      document.body.style.overflow = '';
      if (release) release();
      openers.forEach((o) => o.setAttribute('aria-expanded', 'false'));
      const opener = openers[0];
      if (opener) opener.focus();
    };
    openers.forEach((o) => o.addEventListener('click', open));
    modal.querySelectorAll(closeSel).forEach((c) => c.addEventListener('click', close));
    modal.addEventListener('click', (e) => {
      if (e.target === modal) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
    return { open, close };
  }

  bindModal({
    openSel: '[data-menu-open]',
    closeSel: '[data-menu-close]',
    modalSel: '[data-mobile-menu]',
  });

  const search = bindModal({
    openSel: '[data-search-open]',
    closeSel: '[data-search-close]',
    modalSel: '[data-search-modal]',
    inputSel: '[data-predictive-input]',
  });

  /* ---------- predictive search ---------- */
  (function predictive() {
    const input = document.querySelector('[data-predictive-input]');
    const results = document.querySelector('[data-predictive-results]');
    if (!input || !results) return;
    const run = utils.debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) {
        results.innerHTML = '';
        return;
      }
      try {
        const url = `${Pamper.routes.predictive_search}?q=${encodeURIComponent(
          q
        )}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url);
        const text = await res.text();
        // Prefer JSON endpoint when section unavailable; fallback to suggest.json
        const json = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`
        ).then((r) => r.json());
        const products = (json.resources.results.products || []).slice(0, 6);
        results.innerHTML = products.length
          ? products
              .map(
                (p) => `
          <a class="search-result" href="${p.url}">
            <img src="${p.featured_image ? p.featured_image.url : ''}" alt="" width="48" height="48" loading="lazy">
            <span>${p.title}</span>
            <span class="text-soft">${p.price}</span>
          </a>`
              )
              .join('')
          : `<p class="text-soft">${(Pamper.strings && Pamper.strings.noResults) || 'No matches'}</p>`;
      } catch (err) {
        results.innerHTML = '';
      }
    }, 220);
    input.addEventListener('input', run);
  })();

  /* ---------- newsletter inline feedback ---------- */
  document.querySelectorAll('[data-newsletter-form]').forEach((form) => {
    form.addEventListener('submit', () => {
      // Native Shopify customer form posts; show optimistic toast.
      Pamper.toast('Thanks! Almost there — confirm in your inbox ✨');
    });
  });

  document.addEventListener('DOMContentLoaded', () => document.body.classList.add('is-ready'));
})();
